import React from 'react';
import {
  Link,
  List,
  ListItem,
  ListItemText,
  ListItemTextProps,
  ListSubheader,
  styled,
  Switch,
  Typography,
} from '@mui/material';
import { SETTINGS_ITEM_MAX_WIDTH } from 'themes/constants';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { TFunction } from 'i18next';
import { ConsentModuleType, SignedConsentModule } from 'features/settings/types/types';
import { useSecuritySettings } from 'features/settings/hooks/useSecuritySettings';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';

const ResponsiveListItem = styled(ListItem)(({ theme }) => ({
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    maxWidth: SETTINGS_ITEM_MAX_WIDTH,
  },
}));

const ResponsiveListItemText = styled(ListItemText)(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
  [theme.breakpoints.between('sm', 'md')]: {
    maxWidth: '65%',
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: '75%',
  },
}));

type ListItemTextWithLinkProps = ListItemTextProps & {
  linkText: string;
  linkHref: string;
};

const ListItemTextWithLink = ({
  id,
  primary,
  secondary,
  linkText,
  linkHref,
  ...rest
}: ListItemTextWithLinkProps) => (
  <ResponsiveListItemText
    id={id}
    primary={primary}
    primaryTypographyProps={{ variant: 'callout' }}
    secondary={
      <>
        {`${secondary} `}
        <Link
          href={linkHref}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ textDecoration: 'none' }}
        >
          {linkText}
        </Link>
      </>
    }
    secondaryTypographyProps={{ variant: 'footnote' }}
    {...rest}
  />
);

const getPreferenceTitle = (moduleType: ConsentModuleType, translation: TFunction) => {
  switch (moduleType) {
    case 'logging':
      return translation('policy_heading_usage_data');
    case 'email_contact':
      return translation('policy_heading_emails');
    case 'data_reporting':
      return translation('policy_heading_data_reporting');
    default:
      return '';
  }
};

const getModuleLabelId = (module: SignedConsentModule) =>
  `security__settings-opt-in-${module.type}`;

const DataPreferences = () => {
  const { isDesktop } = useDeviceType();
  const { translation } = useTranslationContext();
  const { modules, handleSignatureChange } = useSecuritySettings();

  return (
    <>
      {isDesktop && (
        <ListSubheader>
          <Typography variant="callout" fontWeight={700} color="grey.800">
            {translation('data_usage_preferences')}
          </Typography>
        </ListSubheader>
      )}
      {modules?.map((module, index) => (
        <ResponsiveListItem key={module.id} divider={index < modules.length - 1}>
          <ListItemTextWithLink
            id={getModuleLabelId(module)}
            primary={getPreferenceTitle(module.type, translation) || module.short_headline}
            secondary={module.body}
            linkText={module.link_text || module.link_url}
            linkHref={module.link_url}
          />
          <Switch
            inputProps={{
              'aria-labelledby': `${getModuleLabelId(module)}`,
            }}
            checked={module.consented}
            onChange={(event) => {
              handleSignatureChange(module.id, event.target.checked);
            }}
          />
        </ResponsiveListItem>
      ))}
    </>
  );
};

export default DataPreferences;
